// stores/home.ts
//
// API Integrations v4 mapping (see docs/v4-migration/API-COVERAGE.md):
//   GET Home/Index  -> WebApi.Integrations.v4.HomeController.IndexAsync
//                      (Helper.Core.Integrations.v4.HomeService), response
//                      BaseResult<HomeResponse>.
//
// v2 -> v4 changes that mattered for this store:
//   - Response envelope: IResultView{isValid, code, model, messages} ->
//     BaseResult<T>{model, correlationId, message}. The `model` field name
//     is unchanged, so the existing double `.model.model` access pattern
//     (base.get()'s own envelope, then the backend's own envelope) still
//     works unmodified.
//   - HomeResponse.Screens / .Links are WebsiteNavigationItemDto
//     {title, webContent, webPageTypeId, isHeader} in both v2 and v4 (this
//     was never {id, name, component} - the previous `Screen` TypeScript
//     interface did not match either backend, v2 included; fixed below as a
//     pre-existing defect unrelated to the version migration).
//   - Home/AboutUs and Home/ContactUs do not exist in v4 under HomeController
//     or anywhere else in Integrations v4 (confirmed by reading
//     WebApi/Integrations/v4/*.cs in full - HomeController only exposes
//     Index; WebsiteContentController exposes Blogs/ClientOpinions/
//     AskedQuestions, not About/Contact copy). This is a genuine, disclosed
//     V4 BACKEND GAP - see docs/v4-migration/BROKEN-FEATURES-FOUND.md. It is
//     non-blocking for this migration because getAboutUs()/getContactUs()
//     were never called by any view (about.vue and contact.vue are fully
//     static) - confirmed by a repo-wide grep before removing them, per the
//     mission's "investigate dynamic registration before deleting" rule.
//     They are intentionally NOT migrated to a fake endpoint; if the About/
//     Contact pages are ever meant to pull CMS content, that needs a real
//     new v4 endpoint, not a client-side workaround.
import { defineStore } from "pinia";
import { useBaseStore } from "./base/index";
import { ref } from "vue";

export interface SiteSettings {
  webPageSettingId: number;
  webSiteImage: string;
  webSiteLogo: string | null;
  webSiteWhiteLogo: string | null;
  contactUs: string | null;
  webSiteBackgroundColor: string;
  webSiteLayoutColor: string;
  isActivePriceAndOffers: boolean;
  isActiveRecruitmentRequest: boolean;
  isActiveAvailableCvs: boolean;
  isActiveHomeVisits: boolean;
  facebookUrl: string;
  instagramUrl: string;
  twitterUrl: string;
  googleSiteVerification: string | null;
  googleTagManagerId: string;
  meta_Description: string;
  meta_keywords: string;
  organizationDescription: string;
  availableServicesAndOffersDescription: string | null;
  expertsInDescription: string;
  visionInDescription: string;
  recruitmentDescription: string;
  businessSectorDescription: string;
  individualSectorDescription: string;
  recruitmentCountriesDescription: string;
  whatsAppNumber: string;
}

/** Matches WebsiteNavigationItemDto (Helper/Core/Integrations/v4/models.cs). */
export interface WebsiteNavigationItem {
  title: string;
  webContent: string;
  webPageTypeId: number;
  isHeader: boolean;
}

export interface Country {
  nationalityId: number;
  name: string;
  isoCode: string;
}

export interface HomeIndexResponse {
  siteSettings: SiteSettings;
  screens: WebsiteNavigationItem[];
  links: WebsiteNavigationItem[];
  countries: Country[];
}

export const useHomeStore = defineStore("Home", () => {
  const base = useBaseStore();
  base.ApiEndPoint("Home");

  const countries = ref<Country[]>([]);
  const links = ref<WebsiteNavigationItem[]>([]);
  const screens = ref<WebsiteNavigationItem[]>([]);
  const siteSettings = ref<SiteSettings | null>(null);
  const loaded = ref(false);

  const getHome = async (): Promise<void> => {
    const result = await base.get<HomeIndexResponse>("Index");
    if (result.isValid && result.model) {
      countries.value = result.model.countries ?? [];
      links.value = result.model.links ?? [];
      screens.value = result.model.screens ?? [];
      siteSettings.value = result.model.siteSettings ?? null;
      loaded.value = true;
    }
  };

  const getNationalityById = (id: number): Country | null =>
    countries.value.find((country) => country.nationalityId === id) || null;

  return {
    ...base,
    getHome,
    countries,
    getNationalityById,
    links,
    screens,
    siteSettings,
    loaded,
  };
});
